/**
 * The top-bar Messages envelope (`[data-sms-envelope]`, templates/includes/_shell-topbar.html and
 * the legacy includes/navbar/_sms_envelope.html) and the General text toast.
 *
 * Counts: refetched as JSON from the envelope's `data-sms-envelope` URL (sms.views.
 * sms_envelope_counts, only the sides the user can open, plus `email` with Acquisitions) and
 * written to each `[data-sms-count="<key>"]` badge (hidden at 0); the classes in `data-sms-dot`
 * show on the trigger while any count is above 0. Refetched, debounced, on:
 *  - `sms:envelope-stale` from rental-sms-ws.js: a `ws/sms/` frame (after a visible General
 *    conversation has re-rendered and marked the text read; the `{envelope: true}` ping also
 *    follows an acqs email count change, `sms.envelope.broadcast_envelope_changed`) or a
 *    reconnect;
 *  - `rentalSmsUnreadChanged` and `messagesInboxChanged` (a thread read, archived or refreshed);
 *  - `sheet:ready` (a General or acqs lead sheet marks its thread read on open);
 *  - navigation and the tab becoming visible, since the top bar outlives htmx page swaps.
 *
 * Toast: `sms:alert` from rental-sms-ws.js carries a General inbound for a `pm_sms_alert` user
 * (`{id, number, text, sheet_url}`; the server gates who gets it, sms/consumers.py), already skipped
 * when that conversation is open, or a new acqs email for a toast recipient (`{id: 'email-<pk>',
 * text, sheet_url}`, acqs.services.email_inbox.email_alert). It shows a clickable info toast that
 * opens `sheet_url` (the General sheet, or the email thread sheet), and plays the attention sound
 * (attention-sound.js) from the envelope's `data-attention-sound`.
 * Every open tab gets the alert, so a tab claims the alert id in localStorage first; a hidden
 * tab waits briefly so a visible one wins.
 */
import { playAttentionSound, unlockAudio } from './attention-sound.js';

(function () {
    var DEBOUNCE_MS = 300;
    var TOAST_MS = 10000;
    var timer = null;

    function envelopes() {
        return document.querySelectorAll('[data-sms-envelope]');
    }

    function soundUrl() {
        var el = document.querySelector('[data-sms-envelope][data-attention-sound]');
        return el && el.getAttribute('data-attention-sound');
    }

    function apply(counts) {
        var any = Object.keys(counts).some(function (side) { return counts[side] > 0; });
        envelopes().forEach(function (envelope) {
            envelope.querySelectorAll('[data-sms-count]').forEach(function (badge) {
                var count = counts[badge.getAttribute('data-sms-count')] || 0;
                badge.textContent = count;
                badge.hidden = !count;
            });
            envelope.querySelectorAll('[data-sms-dot]').forEach(function (el) {
                el.getAttribute('data-sms-dot').split(/\s+/).forEach(function (cls) {
                    if (cls) el.classList.toggle(cls, any);
                });
            });
        });
    }

    function fetchCounts() {
        timer = null;
        var envelope = document.querySelector('[data-sms-envelope]');
        if (!envelope) return;
        fetch(envelope.getAttribute('data-sms-envelope'), { headers: { 'HX-Request': 'true' } })
            .then(function (r) {
                if (!r.ok) throw new Error('SMS envelope counts ' + r.status);
                return r.json();
            })
            .then(apply)
            .catch(function (err) { console.warn('SMS envelope refresh failed', err); });
    }

    function refresh() {
        if (!document.querySelector('[data-sms-envelope]')) return;
        clearTimeout(timer);
        timer = setTimeout(fetchCounts, DEBOUNCE_MS);
    }

    document.addEventListener('sms:envelope-stale', refresh);
    document.body.addEventListener('rentalSmsUnreadChanged', refresh);
    document.body.addEventListener('messagesInboxChanged', refresh);
    document.addEventListener('sheet:ready', refresh);
    document.body.addEventListener('htmx:pushedIntoHistory', refresh);
    window.addEventListener('popstate', refresh);
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') refresh();
    });

    // ── Text and email toast ───────────────────────────────────────────────

    function unlock() { unlockAudio(soundUrl()); }
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });

    // SeeMe.toast returns the toast element but has no click option; this adds one without
    // touching the vendored static/js/seeme/toasts.js. A click dismisses the toast the way its
    // timer does (`toast--leaving`, removed after the animation).
    function clickableToast(message, onClick) {
        var el = SeeMe.toast.info(message, { duration: TOAST_MS });
        el.setAttribute('role', 'button');
        el.tabIndex = 0;
        el.style.cursor = 'pointer';
        function activate() {
            onClick();
            el.classList.add('toast--leaving');
            el.addEventListener('animationend', function () { el.remove(); });
        }
        el.addEventListener('click', activate);
        el.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); }
        });
        return el;
    }

    var CLAIM_PREFIX = 'sms-alert-';
    var CLAIM_TTL_MS = 60000;
    var HIDDEN_WAIT_MS = 300;

    // True when this tab is the first to claim the alert; false when another tab already did.
    // Without localStorage every tab alerts.
    function claim(id) {
        try {
            var now = Date.now();
            for (var i = localStorage.length - 1; i >= 0; i--) {
                var k = localStorage.key(i);
                if (k && k.indexOf(CLAIM_PREFIX) === 0 && now - Number(localStorage.getItem(k)) > CLAIM_TTL_MS) {
                    localStorage.removeItem(k);
                }
            }
            if (localStorage.getItem(CLAIM_PREFIX + id)) return false;
            localStorage.setItem(CLAIM_PREFIX + id, String(now));
        } catch (e) { /* storage unavailable: alert anyway */ }
        return true;
    }

    function showAlert(alert) {
        if (alert.id != null && !claim(alert.id)) return;
        clickableToast(alert.text, function () {
            if (window.rfSheet) window.rfSheet.open(alert.sheet_url);
        });
        playAttentionSound(soundUrl());
    }

    document.addEventListener('sms:alert', function (event) {
        var alert = event.detail;
        if (!window.SeeMe || !SeeMe.toast || !alert || !alert.text) return;
        if (document.hidden) setTimeout(function () { showAlert(alert); }, HIDDEN_WAIT_MS);
        else showAlert(alert);
    });
})();
